import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Font,
} from "@react-pdf/renderer";
import logo from "../../assets/logo2.png";

import MontserratRegular from "../../assets/fonts/Montserrat-Regular.ttf";
import MontserratBold from "../../assets/fonts/Montserrat-Bold.ttf";
import OpenSansRegular from "../../assets/fonts/OpenSans-Regular.ttf";
import OpenSansSemiBold from "../../assets/fonts/OpenSans-SemiBold.ttf";
import OpenSansBold from "../../assets/fonts/OpenSans-Bold.ttf";

Font.register({
  family: "OpenSans",
  fonts: [
    { src: OpenSansRegular, fontWeight: "normal" },
    { src: OpenSansBold, fontWeight: "bold" },
    { src: OpenSansSemiBold, fontWeight: "semi-bold" },
  ],
});

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 12,
    fontFamily: "OpenSans",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottomWidth: 1,
    borderColor: "#000",
    marginBottom: 5, // Increased spacing
    paddingBottom: 5,
  },
  logo: { width: 80, height: 96 },
  title: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 2,
    textAlign: "center",
    width: 220,
  },
  section: { marginBottom: 2 }, // Increased spacing
  sectionTitle: { fontSize: 14, fontWeight: "bold", marginBottom: 2 },
  sectionSecondTitle: { fontSize: 12, fontWeight: 500, marginBottom: 2 },
  text: { lineHeight: 1.5 },
  table: {
    // Increased spacing
    borderWidth: 1,
    borderColor: "#000",
    marginBottom: 10, // Increased spacing
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderColor: "#000",
  },
  tableCell: {
    flex: 1,
    padding: 5,
    borderRightWidth: 1,
    borderColor: "#000",
    fontSize: 12,
  },
  tableCellText: {
    width: 80,
    padding: 5,
    borderRightWidth: 1,
    borderColor: "#000",
    fontSize: 12,
    textAlign: "center",
  },
  tableCellLast: {
    flex: 1,
    padding: 5,
    fontSize: 12,
  },
  dottedDivider: {
    width: "100%",
    textAlign: "center",
    fontSize: 12, // Spacing above and below the line
    letterSpacing: 3, // Spacing above and below the line
  },
  contentRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 5, // Increased spacing
  },
  leftColumn: {
    width: "30%",
    paddingRight: 10,
    borderRightWidth: 1,
    borderColor: "#000",
  },
  rightColumn: {
    width: "70%",
    paddingLeft: 10,
  },
  doctorName: {
    fontWeight: "600",
    fontSize: 12,
  },
  tempName: {
    fontWeight: "bold",
    fontSize: 14,
    marginTop: 10,
  },
  doctorQualification: {
    fontSize: 10,
  },
  doctorDetails: {
    fontSize: 12,
  },
  divider: {
    height: 1,
    backgroundColor: "#000",
    marginVertical: 10,
  },
  marginBottomtemp: {
    marginBottom: 5,
  },
  verticalText: {
    marginTop: -200,
    marginLeft: 265,
    position: "absolute",
    transform: "rotate(-45deg)",
    fontSize: 18,
    color: "rgba(0, 0, 0, 0.1)",
    letterSpacing: 3,
    wordSpacing: 10,
    fontWeight: 500,
    zIndex: -1,
    pointerEvents: "none",
  },
  footerText: {
  marginTop: 10,
  textAlign: "center",
  fontSize: 10,
},
});

const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return "N/A";
  const birthDate = new Date(dateOfBirth);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }
  return age;
};

const formatDate = (dateString) => {
  if (!dateString) return ""; // Handle null or undefined
  const options = { year: "numeric", month: "short", day: "numeric" };
  return new Date(dateString).toLocaleDateString("en-US", options);
};

const PatientReport = ({ patient, doctor }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      {/* Header Section */}
      <View style={styles.header}>
        {/* Logo */}
        <View
          style={{
            flexDirection: "row",
            alignItems: "left",
            flex: 1,
            justifyContent: "left",
          }}
        >
          <Image style={styles.logo} src={logo} />
        </View>
        {/* Center Title */}
        <View
          style={{
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            flex: 2,
          }}
        >
          <Text style={styles.title}>Hospital Management System</Text>
          <Text style={styles.address}> Lahore</Text>
          <Text style={styles.address}>0300-0000000</Text>
        </View>
        {/* Doctor Information */}
        <View
          style={{
            flexDirection: "column",
            justifyContent: "flex-end",
            alignItems: "flex-end",
            flex: 1,
          }}
        >
          <Text style={styles.doctorName}>{patient.doctor_name}</Text>
          <Text style={styles.doctorQualification}>
            {patient.qualification}
          </Text>
          <Text style={styles.doctorQualification}>
            {patient.specialization}
          </Text>
          <Text style={{ fontSize: 12, marginBottom: 5 }}>
          {patient.pmdc_no || "-"}  
        </Text>
        </View>
      </View>
      {/* Patient Details */}
      <View
        style={[
          styles.section,
          {
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
          },
        ]}
      >
        <Text style={styles.sectionTitle}>
          Patient Name:{" "}
          {patient?.patient_name
            ?.split(" ")
            .map(
              (word) =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            )
            .join(" ")}
        </Text>
        <View style={styles.section}>
          <Text style={styles.doctorDetails}>
            {/* {formatDate(patient?.sale_date)}
            */}
            {formatDate(patient.sale_date) || formatDate(patient.checkup_date) || formatDate(patient.date) || "N/A"}

          </Text>
          <Text style={styles.doctorDetails}>
            Patient No: {patient.patient_id || "0000"}
          </Text>
        <Text style={styles.doctorDetails}>
          Prescription No: {patient.sale_unique_id || "—"}
        </Text>
        </View>
      </View>

      {/* Table Section */}
      <View style={styles.table}>
        <View style={styles.tableRow}>
          <Text style={styles.tableCellText}>Age</Text>
          <Text style={styles.tableCell}>
            {patient.age || calculateAge(patient.date_of_birth)}
          </Text>
          <Text style={styles.tableCellText}>Gender</Text>
          <Text style={styles.tableCell}>{patient.gender || "Male"}</Text>
        </View>
      </View>

      <View
        style={[
          styles.section,
          { flexDirection: "row", justifyContent: "space-between" },
        ]}
      >
        <Text style={styles.sectionSecondTitle}>Temp:</Text>
        <Text style={styles.sectionSecondTitle}>B/P:</Text>
        <Text style={[styles.sectionSecondTitle, { marginRight: 100 }]}>
          {" "}
          Pulse Rate{" "}
        </Text>
      </View>

      <View style={styles.divider} />

      {/* History, Diagnose, Recommendation */}
      <View style={styles.contentRow}>
        <View style={styles.leftColumn}>
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>History</Text>
            <Text style={styles.text}>{patient.history || "\n\n\n\n\n"}</Text>
          </View>
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Diagnose</Text>
            <Text style={styles.text}>{patient.diagnosis || "\n\n\n\n\n"}</Text>
          </View>
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Recommendation</Text>
            <Text style={styles.text}>{patient.diagnosis || "\n\n\n\n\n"}</Text>
          </View>
        </View>
      </View>

      <View>
        <Text style={styles.verticalText}>Not valid for Court</Text>
      </View>
      <Text style={styles.dottedDivider}>
        -----------------------------------------------------------------------------------
      </Text>

      {/* Headings Below the Dotted Line */}
      <View
        style={{
          flexDirection: "row",
          marginTop: 5,
        }}
      >
        <Text style={[styles.text, { flex: 1, textAlign: "left" }]}>
          Patient No: {patient.patient_id || "NULL"}
        </Text>
        <Text style={[styles.sectionTitle, { flex: 1, textAlign: "center" }]}>
          Hospital Medicines
        </Text>
        <Text style={[styles.text, { flex: 1, textAlign: "right" }]}>
          {/* {formatDate(patient.sale_date) || "N/A"} */}
         
{formatDate(patient.sale_date) || formatDate(patient.checkup_date) || formatDate(patient.date) || "N/A"}

        </Text>
      </View>

      {/* Patient Data Below Headings */}
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Text style={[styles.text, { flex: 1, textAlign: "left" }]}>
          {patient.patient_name || "0000"}
        </Text>
        <Text style={[styles.text, { flex: 1, textAlign: "right" }]}>
          {patient.doctor_name || "0000"}
        </Text>
        
      </View>
      <View style={{ marginTop: 10 }}>
  <Text style={styles.footerText}>
    You can get your prescriptions on https://demo.hms-pk.com/
  </Text>
  <Text style={styles.footerText}>
    by adding your Prescription ID: {patient.sale_unique_id || "—"}
  </Text>
</View>
    </Page>
  </Document>
);

export default PatientReport;
